import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PLAFOND_DEPOT_DEFAUT_OCTETS } from '@artosera/shared';
import { Badge, Button, Input, type BadgeTone } from '@artosera/ui';
import {
  creerDepot,
  depotEnTravail,
  inventorierDepot,
  listerDepots,
  octetsEnGio,
  type DepotVue,
  type EtatInventaire,
} from '../../../lib/depots.js';
import { formaterDate, formaterOctets } from '../../../lib/format.js';
import formStyles from '../../../styles/form.module.css';
import pageStyles from '../../../styles/page.module.css';
import { EnvoiDepot } from './EnvoiDepot.js';
import { ManifesteDepot } from './ManifesteDepot.js';
import { messageReprise } from './messages.js';
import styles from './Reprise.module.css';

export function cleDepots(idGalerie: string): readonly string[] {
  return ['console', 'galerie', idGalerie, 'reprise', 'depots'];
}

const TON_INVENTAIRE: Record<EtatInventaire, BadgeTone> = {
  vide: 'neutral',
  a_faire: 'reserved',
  en_cours: 'reserved',
  fait: 'available',
  bloque: 'sold',
};

const PLAFOND_DEFAUT_GIO = PLAFOND_DEPOT_DEFAUT_OCTETS / 1024 ** 3;

/** « Nouveau dépôt » : un nom, un plafond. Les fichiers viennent ensuite. */
function NouveauDepot({
  idGalerie,
  onCree,
  onAnnuler,
}: {
  idGalerie: string;
  onCree: (depot: DepotVue) => void;
  onAnnuler: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [libelle, setLibelle] = useState('');
  const [plafond, setPlafond] = useState(String(PLAFOND_DEFAUT_GIO));
  const creer = useMutation({
    mutationFn: () =>
      creerDepot(idGalerie, {
        libelle: libelle.trim(),
        plafondOctets: octetsEnGio(Number(plafond) || PLAFOND_DEFAUT_GIO),
      }),
    onSuccess: onCree,
  });

  const soumettre = (evenement: FormEvent) => {
    evenement.preventDefault();
    creer.mutate();
  };

  return (
    <form className={styles.carte} onSubmit={soumettre}>
      <div className={formStyles.grille}>
        <div className={formStyles.champ}>
          <label htmlFor="depot-libelle">{t('reprise.depots.libelle')}</label>
          <Input
            id="depot-libelle"
            value={libelle}
            maxLength={200}
            onChange={(evenement) => setLibelle(evenement.target.value)}
          />
          <span className={formStyles.aide}>{t('reprise.depots.libelleAide')}</span>
        </div>
        <div className={formStyles.champ}>
          <label htmlFor="depot-plafond">{t('reprise.depots.plafond')}</label>
          <Input
            id="depot-plafond"
            type="number"
            inputMode="decimal"
            min={0.001}
            max={200}
            step="any"
            value={plafond}
            onChange={(evenement) => setPlafond(evenement.target.value)}
          />
          <span className={formStyles.aide}>{t('reprise.depots.plafondAide')}</span>
        </div>
      </div>
      {creer.isError ? (
        <p className={styles.erreur} role="alert">
          {messageReprise(t, creer.error)}
        </p>
      ) : null}
      <div className={styles.actions}>
        <Button type="submit" loading={creer.isPending}>
          {t('reprise.depots.creer')}
        </Button>
        <Button variant="ghost" onClick={onAnnuler}>
          {t('reprise.depots.annuler')}
        </Button>
      </div>
    </form>
  );
}

/** Un dépôt : son volume, son inventaire, ses gestes. */
function CarteDepot({
  idGalerie,
  depot,
  envoiOuvert,
  designe,
  onEnvoi,
}: {
  idGalerie: string;
  depot: DepotVue;
  envoiOuvert: boolean;
  /** Le lien qui a mené ici (`#depot-…`) vise ce dépôt : il est mis en avant. */
  designe: boolean;
  onEnvoi: (ouvert: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const [manifeste, setManifeste] = useState(false);
  const relancer = useMutation({
    mutationFn: () => inventorierDepot(idGalerie, depot.id),
    onSettled: () => client.invalidateQueries({ queryKey: cleDepots(idGalerie) }),
  });
  const compteurs = depot.inventaire.compteurs;
  const titre = depot.libelle || t('reprise.depots.sansNom');

  return (
    <li
      id={`depot-${depot.id}`}
      className={styles.carte}
      aria-label={titre}
      aria-current={designe ? 'true' : undefined}
    >
      <div className={styles.rangee}>
        <h3 className={styles.sousTitre}>{titre}</h3>
        <Badge tone={TON_INVENTAIRE[depot.inventaire.etat]}>
          {t(`reprise.depots.inventaire.${depot.inventaire.etat}`)}
        </Badge>
        {depot.etat !== 'ouvert' ? (
          <Badge tone="withdrawn">{t(`reprise.depots.etats.${depot.etat}`)}</Badge>
        ) : null}
      </div>
      <p className={styles.discret}>
        {[
          t('reprise.depots.volume', {
            recus: formaterOctets(depot.octetsRecus),
            plafond: formaterOctets(depot.plafondOctets),
          }),
          t('reprise.depots.fichiers', { count: depot.fichiers }),
          depot.lienId === null ? null : t('reprise.depots.parLien'),
          `${depot.operateur}, ${formaterDate(depot.creeLe, '—')}`,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {compteurs !== null ? (
        <p className={styles.texte}>{t('reprise.depots.compteurs', { ...compteurs })}</p>
      ) : null}
      {depot.inventaire.etat === 'bloque' ? (
        <p className={styles.avertissement} role="status">
          {t('reprise.depots.bloque')}
        </p>
      ) : null}
      {relancer.isError ? (
        <p className={styles.erreur} role="alert">
          {messageReprise(t, relancer.error)}
        </p>
      ) : null}
      <div className={styles.actions}>
        {depot.etat === 'ouvert' ? (
          <Button
            variant={envoiOuvert ? 'ghost' : 'secondary'}
            onClick={() => onEnvoi(!envoiOuvert)}
          >
            {envoiOuvert ? t('reprise.depots.fermerEnvoi') : t('reprise.depots.envoyer')}
          </Button>
        ) : null}
        {depot.inventaire.manifeste ? (
          <Button variant="ghost" onClick={() => setManifeste((courant) => !courant)}>
            {manifeste ? t('reprise.depots.masquerManifeste') : t('reprise.depots.voirManifeste')}
          </Button>
        ) : null}
        {depot.inventaire.etat === 'bloque' ? (
          <Button variant="ghost" loading={relancer.isPending} onClick={() => relancer.mutate()}>
            {t('reprise.depots.relancer')}
          </Button>
        ) : null}
      </div>
      {envoiOuvert ? (
        <EnvoiDepot
          idGalerie={idGalerie}
          depotId={depot.id}
          onFin={() => void client.invalidateQueries({ queryKey: cleDepots(idGalerie) })}
        />
      ) : null}
      {manifeste && depot.inventaire.manifeste ? (
        <ManifesteDepot idGalerie={idGalerie} depotId={depot.id} />
      ) : null}
    </li>
  );
}

/**
 * Fiche galerie › Reprise › Dépôts (ARTO-129) : l'export brut de l'ancien
 * logiciel, fichiers et dossiers, avant toute correspondance. Un dépôt
 * n'écrit rien chez la galerie ; en faire un lot viendra avec le profilage.
 */
export function DepotsBloc({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t } = useTranslation('console');
  const client = useQueryClient();
  const [nouveau, setNouveau] = useState(false);
  const [envoi, setEnvoi] = useState<string | null>(null);

  const depots = useQuery({
    queryKey: cleDepots(idGalerie),
    queryFn: () => listerDepots(idGalerie),
    // Un inventaire qui tourne se suit, comme un lot.
    refetchInterval: (requete) => (requete.state.data?.some(depotEnTravail) ? 3000 : false),
  });

  // Un lien `#depot-…` (le suivi d'un import par connecteur) amène au dépôt visé.
  const { hash } = useLocation();
  const liste = depots.isSuccess;
  useEffect(() => {
    if (liste && hash.startsWith('#depot-'))
      document.getElementById(hash.slice(1))?.scrollIntoView?.({ block: 'center' });
  }, [hash, liste]);

  return (
    <section className={pageStyles.panneau} aria-labelledby="reprise-depots">
      <div className={pageStyles.sectionTitre}>
        <h2 id="reprise-depots">{t('reprise.depots.titre')}</h2>
        {!nouveau ? (
          <Button onClick={() => setNouveau(true)}>{t('reprise.depots.nouveau')}</Button>
        ) : null}
      </div>
      <div className={styles.pile}>
        <p className={styles.texte}>{t('reprise.depots.texte')}</p>
        {nouveau ? (
          <NouveauDepot
            idGalerie={idGalerie}
            onAnnuler={() => setNouveau(false)}
            onCree={(depot) => {
              setNouveau(false);
              setEnvoi(depot.id);
              void client.invalidateQueries({ queryKey: cleDepots(idGalerie) });
            }}
          />
        ) : null}
        {depots.isLoading ? <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p> : null}
        {depots.isError ? (
          <p className={styles.erreur} role="alert">
            {t('reprise.depots.erreur')}
          </p>
        ) : null}
        {depots.isSuccess && depots.data.length === 0 ? (
          <p className={pageStyles.videEtat}>{t('reprise.depots.aucun')}</p>
        ) : null}
        {depots.isSuccess && depots.data.length > 0 ? (
          <ul className={styles.cartes}>
            {depots.data.map((depot) => (
              <CarteDepot
                key={depot.id}
                idGalerie={idGalerie}
                depot={depot}
                envoiOuvert={envoi === depot.id}
                designe={hash === `#depot-${depot.id}`}
                onEnvoi={(ouvert) => setEnvoi(ouvert ? depot.id : null)}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
